import { useState, useEffect } from "react";
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useAccount, useWaitForTransactionReceipt } from 'wagmi';
import { AppSidebar } from "@/components/app-sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/breadcrumb";
import { Separator } from "@/components/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/sidebar";
import { Link } from "react-router-dom";
import { Button } from "@/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/tabs";
import { Input } from "@/components/input";
import {
    RiNodeTree,
    RiLockLine,
    RiShareLine,
    RiFileCopyLine,
    RiAddLine,
    RiWallet3Line,
    RiLoader4Line
} from "@remixicon/react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/table";
import { LRSNODE_CONTRACT_CONFIG } from "@/configs/lrsnode.config";
import { useNodeActions } from "@/hooks/use-node-actions";
import { useLRSNode } from "@/providers/global-data.provider";
import BuyNodeModal from "@/components/buy-node-modal";
import CreateReferCodeModal from "@/components/create-refer-code-modal";

export default function MyNodesPage() {
  const { address: userAccount } = useAccount();
  const [isMounted, setIsMounted] = useState(false);

  // Get all LRSNode data from global provider
  const {
    userReferCode,
    slotData,
    walletBalance,
    nodes,
    referNodes,
    lockRewards,
    isLoadingNodes,
    isLoadingReferNodes,
    isLoadingLockRewards,
    refreshNodes,
  } = useLRSNode();

  const { handleClaimNodeReward, handleClaimLockedReward, handleClaimReferRewards } = useNodeActions();
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [renewNodeKey, setRenewNodeKey] = useState<string | undefined>(undefined);
  const [showCreateReferModal, setShowCreateReferModal] = useState(false);
  const [claimingNodeKey, setClaimingNodeKey] = useState<string | null>(null);
  const [currentTxHash, setCurrentTxHash] = useState<`0x${string}` | undefined>(undefined);

  // Prevent hydration mismatch
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Transaction receipt hook
  const { isSuccess: isTxSuccess } = useWaitForTransactionReceipt({
    hash: currentTxHash,
    query: {
      enabled: !!currentTxHash,
    },
  });

  // Refresh all data
  const refreshEverything = () => {
    refreshNodes();
  };

  // Track transaction success
  useEffect(() => {
    if (isTxSuccess && currentTxHash) {
      refreshNodes();
      setCurrentTxHash(undefined);
      setClaimingNodeKey(null);
      refreshEverything();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTxSuccess, currentTxHash]);

  useEffect(() => {
    if (userReferCode && userReferCode.length > 0) {
      setShowCreateReferModal(false);
      refreshEverything();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userReferCode]);

  // Format functions
  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatBalance = (balance?: bigint) => {
    if (!balance) return '0';
    return (Number(balance) / 1e18).toFixed(4);
  };

  const formatDate = (timestamp: bigint) => {
    return new Date(Number(timestamp) * 1000).toLocaleString();
  };

  const isNodeExpired = (expireTime: bigint) => {
    const now = Date.now();
    const currentTime = BigInt(Math.floor(now / 1000));
    return currentTime >= expireTime;
  };

  const getExpirationStatusColor = (expireTime: bigint) => {
    const now = Date.now();
    const currentTime = BigInt(Math.floor(now / 1000));
    return currentTime < expireTime ? 'text-green-400' : 'text-red-400';
  };

  const formatNodeKey = (nodeKey: string) => {
    if (nodeKey.length <= 16) return nodeKey;
    return `${nodeKey.slice(0, 8)}...${nodeKey.slice(-8)}`;
  };

  const copyToClipboard = (text: string) => {
    if (typeof window !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(text)
        .then(() => {
          alert('Copied to clipboard!');
        })
        .catch(err => {
          console.error('Failed to copy: ', err);
        });
    } else {
      // Fallback for older browsers or SSR
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        alert('Copied to clipboard!');
      } catch (err) {
        console.error('Failed to copy: ', err);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleBuySuccess = async () => {
    await refreshNodes();
    setShowBuyModal(false);
    refreshEverything();
  };

  const handleBuyModalClose = () => {
    setShowBuyModal(false);
    setRenewNodeKey(undefined);
    refreshEverything();
  };

  const onClaimReward = async (nodeKey: string) => {
    try {
      setClaimingNodeKey(nodeKey);
      const tx = await handleClaimNodeReward(nodeKey);
      setCurrentTxHash(tx.hash);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to claim node rewards';
      alert(errorMessage);
      setClaimingNodeKey(null);
    }
    refreshEverything();
  };

  const onRenewNode = async (nodeKey: string) => {
    try {
      setRenewNodeKey(nodeKey);
      setShowBuyModal(true);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to renew node';
      alert(errorMessage);
    }
    refreshEverything();
  };

  const handleClaimReferReward = async (nodeKey: string) => {
    try {
      const tx = await handleClaimReferRewards(nodeKey, userReferCode ?? '');
      setCurrentTxHash(tx.hash);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to claim refer reward');
    }
    refreshEverything();
  };

  const handleClaimLockReward = async (lockId: bigint) => {
    try {
      const tx = await handleClaimLockedReward(lockId);
      setCurrentTxHash(tx.hash);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to claim lock reward');
    }
    refreshEverything();
  };

  const handleCreateReferSuccess = async () => {
    try {
      refreshEverything();
      if (userReferCode) {
        alert('Refer code created successfully!');
        setShowCreateReferModal(false);
      }
    } catch (error) {
      console.error('Failed to refresh refer code:', error);
      alert('Failed to refresh refer code');
    }
    refreshEverything();
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <div className="px-4 md:px-6 lg:px-8 @container">
          <div className="w-full max-w-6xl mx-auto">
            <header className="flex flex-wrap gap-3 min-h-20 py-4 shrink-0 items-center transition-all ease-linear border-b">
              <div className="flex flex-1 items-center gap-2">
                <SidebarTrigger className="-ms-1" />
                <div className="max-lg:hidden lg:contents">
                  <Separator
                    orientation="vertical"
                    className="me-2 data-[orientation=vertical]:h-4"
                  />
                  <Breadcrumb>
                    <BreadcrumbList>
                      <BreadcrumbItem className="hidden md:block">
                        <BreadcrumbLink asChild>
                          <Link to="/">Home</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                          <Link to="/lrsnode">LRSNode</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbPage>My Nodes</BreadcrumbPage>
                      </BreadcrumbItem>
                    </BreadcrumbList>
                  </Breadcrumb>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <ConnectButton />
              </div>
            </header>

            <div className="py-6">
              <div className="flex items-center mb-6">
                <div className="w-1 h-8 bg-[#FF8A3C] mr-3"></div>
                <h1 className="text-3xl font-bold">My Nodes</h1>
              </div>

              <div className="w-full max-w-xs h-1 bg-gradient-to-r from-[#FF8A3C] to-transparent mb-8 rounded-full"></div>

              <Card className="bg-sidebar border border-border mb-8">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Wallet</div>
                      <div className="font-medium flex items-center">
                        <RiWallet3Line className="size-4 mr-2 text-[#FF8A3C]" />
                        <span className="text-sm md:text-base">
                          {!isMounted ? 'Loading...' : userAccount ? formatAddress(userAccount) : 'Not connected'}
                        </span>
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Balance</div>
                      <div className="font-medium text-[#FF8A3C]">
                        {!isMounted ? '0.0000 LRS' : formatBalance(walletBalance)} LRS
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-sidebar border border-border mb-8">
                <CardContent className="p-6">
                  <div className="flex flex-col items-center justify-center py-4">
                    <div className="text-4xl font-bold mb-2">
                      {slotData !== undefined ? slotData.toString().padStart(2, '0') : '00'}
                    </div>
                    <div className="text-sm text-muted-foreground">Available nodes</div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <Card className="bg-sidebar border border-border">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm text-muted-foreground">Your Nodes</div>
                      <RiNodeTree className="size-5 text-[#FF8A3C]" />
                    </div>
                    <div className="text-3xl font-bold">{nodes.length}</div>
                  </CardContent>
                </Card>

                <Card className="bg-sidebar border border-border">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm text-muted-foreground">Referral Nodes</div>
                      <RiShareLine className="size-5 text-[#FF8A3C]" />
                    </div>
                    <div className="text-3xl font-bold">{referNodes.length}</div>
                  </CardContent>
                </Card>

                <Card className="bg-sidebar border border-border">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm text-muted-foreground">Lock Rewards</div>
                      <RiLockLine className="size-5 text-[#FF8A3C]" />
                    </div>
                    <div className="text-3xl font-bold">{lockRewards.length}</div>
                  </CardContent>
                </Card>
              </div>

              <Tabs defaultValue="nodes" className="mb-8">
                <TabsList className="mb-6">
                  <TabsTrigger value="nodes">List of Nodes</TabsTrigger>
                  <TabsTrigger value="rewards">Lock Rewards</TabsTrigger>
                  <TabsTrigger value="referrals">Your Refer Node List</TabsTrigger>
                </TabsList>

                <TabsContent value="nodes">
                  <Card className="bg-sidebar border border-border">
                    <CardHeader>
                      <CardTitle>Your Nodes</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {isLoadingNodes ? (
                        <div className="flex justify-center py-8">
                          <RiLoader4Line className="size-8 animate-spin text-[#FF8A3C]" />
                        </div>
                      ) : nodes.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                          <RiNodeTree size={48} className="mb-4" />
                          <p>No nodes found. Purchase a node to get started.</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Node Key ID</TableHead>
                                <TableHead>Node Key</TableHead>
                                <TableHead>Purchased on</TableHead>
                                <TableHead>Expire on</TableHead>
                                <TableHead>Rewards</TableHead>
                                <TableHead>Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {nodes.map((node) => (
                                <TableRow key={node.nodeKeyID.toString()}>
                                  <TableCell>{node.nodeKeyID.toString()}</TableCell>
                                  <TableCell>
                                    <div className="flex items-center space-x-2">
                                      <span className="font-mono text-xs">{formatAddress(node.nodeKey)}</span>
                                      <button
                                        onClick={() => copyToClipboard(node.nodeKey)}
                                        className="text-[#FF8A3C] hover:text-[#FF8A3C]/80"
                                      >
                                        <RiFileCopyLine size={16} />
                                      </button>
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-xs">{formatDate(node.buyTime)}</TableCell>
                                  <TableCell className="text-xs">
                                    <span className={getExpirationStatusColor(node.expireTime)}>
                                      {formatDate(node.expireTime)}
                                    </span>
                                  </TableCell>
                                  <TableCell>
                                    {node.pendingReward ? formatBalance(node.pendingReward) : '0'} LRS
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <Button
                                        size="sm"
                                        onClick={() => {
                                          if(node.pendingReward && node.pendingReward > 0) {
                                            onClaimReward(node.nodeKey);
                                          } else {
                                            alert('No rewards to claim');
                                          }
                                        }}
                                        disabled={claimingNodeKey === node.nodeKey}
                                        className="bg-[#FF8A3C] hover:bg-[#FF8A3C]/90"
                                      >
                                        {claimingNodeKey === node.nodeKey ? (
                                          <RiLoader4Line className="size-4 animate-spin" />
                                        ) : (
                                          'Claim'
                                        )}
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="rewards">
                  <Card className="bg-sidebar border border-border">
                    <CardHeader>
                      <CardTitle>Lock Rewards</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {isLoadingLockRewards ? (
                        <div className="flex justify-center py-8">
                          <RiLoader4Line className="size-8 animate-spin text-[#FF8A3C]" />
                        </div>
                      ) : lockRewards.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                          <RiLockLine size={48} className="mb-4" />
                          <p>No locked rewards found.</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Lock ID</TableHead>
                                <TableHead>Lock Amount</TableHead>
                                <TableHead>Lock Time</TableHead>
                                <TableHead>Unlock Time</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {lockRewards.map((lock) => (
                                <TableRow key={lock.lockID.toString()}>
                                  <TableCell>{lock.lockID.toString()}</TableCell>
                                  <TableCell>{formatBalance(lock.lockAmount)} LRSN</TableCell>
                                  <TableCell className="text-xs">{formatDate(lock.lockTime)}</TableCell>
                                  <TableCell className="text-xs">{formatDate(lock.unlockTime)}</TableCell>
                                  <TableCell>
                                    <span className={lock.isClaim ? 'text-green-400' : 'text-blue-400'}>
                                      {lock.isClaim ? 'Claimed' : 'Locked'}
                                    </span>
                                  </TableCell>
                                  <TableCell>
                                    <Button
                                      size="sm"
                                      onClick={() => {
                                        handleClaimLockReward(lock.lockID).then();
                                      }}
                                      disabled={lock.isClaim}
                                      className="bg-[#FF8A3C] hover:bg-[#FF8A3C]/90"
                                    >
                                      Unlock
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="referrals">
                  <Card className="bg-sidebar border border-border mb-6">
                    <CardHeader>
                      <CardTitle>Your Refer Node List</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {isLoadingReferNodes ? (
                        <div className="flex justify-center py-8">
                          <RiLoader4Line className="size-8 animate-spin text-[#FF8A3C]" />
                        </div>
                      ) : referNodes.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                          <RiShareLine size={48} className="mb-4" />
                          <p>No referral nodes found. Share your referral code to earn rewards.</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Node Key</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {referNodes.map((node) => (
                                <TableRow key={node.nodeKey}>
                                  <TableCell>
                                    <div className="flex items-center space-x-2">
                                      <span className="font-mono text-xs">{formatNodeKey(node.nodeKey)}</span>
                                      <button
                                        onClick={() => copyToClipboard(node.nodeKey)}
                                        className="text-[#FF8A3C] hover:text-[#FF8A3C]/80"
                                      >
                                        <RiFileCopyLine size={16} />
                                      </button>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <span className={`px-2 py-1 rounded text-xs ${
                                      node.isClaimed
                                        ? 'bg-green-500/20 text-green-400'
                                        : 'bg-blue-500/20 text-blue-400'
                                    }`}>
                                      {node.isClaimed ? 'Claimed' : 'Not Claimed'}
                                    </span>
                                  </TableCell>
                                  <TableCell>
                                    {!node.isClaimed && (
                                      <Button
                                        size="sm"
                                        onClick={() => handleClaimReferReward(node.nodeKey)}
                                        className="bg-[#FF8A3C] hover:bg-[#FF8A3C]/90"
                                      >
                                        Claim Rewards
                                      </Button>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="bg-sidebar border border-border">
                    <CardHeader>
                      <CardTitle>Create Refer Code</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {userReferCode && userReferCode.length > 0 ? (
                        <>
                          <div className="flex flex-col md:flex-row gap-4">
                            <div className="flex-grow">
                              <div className="text-sm text-muted-foreground mb-2">Your Refer Code</div>
                              <div className="flex">
                                <Input
                                  value={userReferCode}
                                  readOnly
                                  className="bg-background font-mono"
                                />
                                <Button
                                  variant="outline"
                                  className="ml-2 hover:border-[#FF8A3C] hover:text-[#FF8A3C]"
                                  onClick={() => copyToClipboard(userReferCode ?? '')}
                                >
                                  <RiFileCopyLine className="size-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground mt-4">
                            Share your refer code with others to earn rewards when they buy nodes.
                          </p>
                        </>
                      ) : (
                        <>
                          <div className="flex justify-center mb-4">
                            <Button
                              onClick={() => setShowCreateReferModal(true)}
                              className="bg-[#FF8A3C] hover:bg-[#FF8A3C]/90"
                            >
                              <RiShareLine className="size-4 mr-2" />
                              Create Refer Code
                            </Button>
                          </div>
                          <p className="text-sm text-muted-foreground text-center">
                            Create a unique refer code to start earning rewards from referrals.
                          </p>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </SidebarInset>

      <BuyNodeModal
        isOpen={showBuyModal}
        nodeKey={renewNodeKey}
        onClose={handleBuyModalClose}
        onSuccess={handleBuySuccess}
      />

      <CreateReferCodeModal
        isOpen={showCreateReferModal}
        onClose={() => setShowCreateReferModal(false)}
        onSuccess={handleCreateReferSuccess}
      />
    </SidebarProvider>
  );
}



